import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Pane, Heading, Text, Spinner, Alert, Code, Button, Card } from 'evergreen-ui';
import { useNavigate } from 'react-router-dom';
import { IS_SUPERUSER } from '../../graphql/queries/Auth/QueryAuth';
import VisitorsBox from './statsBoxes/VisitorsBox';
import InactiveUsersBox from './statsBoxes/InactiveUsersBox';
import ProductsBox from './statsBoxes/ProductsBox';
import CustomersBox from './statsBoxes/CustomersBox';
import SignupsBox from './statsBoxes/SignupsBox';
import SubscriptionsBox from './statsBoxes/SubscriptionsBox';
import SystemStatsBox from './statsBoxes/SystemStatsBox';
import SuperAdminSearchBar from './SearchBar';
import PublisherStatsModal from './PublisherStatsModal';
import SystemHealthMonitor from './charts/SystemHealthMonitor';

/**
 * Super Admin Dashboard component
 * This dashboard is only accessible to superusers
 */
const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [directSuperuserCheck, setDirectSuperuserCheck] = useState(false);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);

  // Direct check for superuser status
  useEffect(() => {
    const checkSuperuser = async () => {
      try {
        const response = await fetch('/api/v1/auth/check_superuser/', {
          method: 'GET',
          headers: {
            'Authorization': `Token ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        console.log('Direct superuser check:', data);
        setDirectSuperuserCheck(data.is_superuser);
      } catch (error) {
        console.error('Error checking superuser status:', error);
      }
    };
    checkSuperuser();
  }, []);

  // Check if the user is a superuser
  const { data, loading, error } = useQuery(IS_SUPERUSER, {
    fetchPolicy: 'no-cache', // Don't use cache at all
    errorPolicy: 'all', // Return data even if there are errors
    onError: (error) => {
      console.error('SuperAdmin GraphQL error:', error);
      setErrorDetails(error);
      setAuthError(true);
    }
  });

  useEffect(() => {
    // Add detailed console logging to debug
    console.log('SuperAdminDashboard - Auth data:', data);
    console.log('SuperAdminDashboard - Loading:', loading);
    console.log('SuperAdminDashboard - Error:', error);
    console.log('SuperAdminDashboard - Token:', localStorage.getItem('token'));
    console.log('SuperAdminDashboard - User ID:', localStorage.getItem('userid'));
    console.log('SuperAdminDashboard - Direct superuser check:', directSuperuserCheck);

    // Only check superuser status if we have data or direct check
    if ((!loading && data) || directSuperuserCheck) {
      if (data) {
        console.log('SuperAdminDashboard - isSuperuser:', data.isSuperuser);
        console.log('SuperAdminDashboard - adminCheck:', data.adminCheck);
      }

      // First try to use isSuperuser, but fall back to adminCheck or directSuperuserCheck if needed
      if ((data && data.isSuperuser === true) ||
          (data && data.isSuperuser === undefined && data.adminCheck === true) ||
          directSuperuserCheck) {
        console.log('Admin check passed, user is a superuser');
        setAuthError(false);
      } else {
        console.log('Admin check failed, showing error message');
        setAuthError(true);
      }
    }
  }, [data, loading, error, directSuperuserCheck]);

  // Handle publisher selection from search
  const handleSelectPublisher = (publisher) => {
    console.log('Dashboard - Selected publisher:', publisher);
    console.log('Dashboard - Publisher ID:', publisher.id);
    console.log('Dashboard - Publisher ID type:', typeof publisher.id);

    // Ensure publisher.id is a number
    const publisherWithNumericId = {
      ...publisher,
      id: parseInt(publisher.id, 10)
    };

    console.log('Dashboard - Converted publisher:', publisherWithNumericId);

    setSelectedPublisher(publisherWithNumericId);
    setIsStatsModalOpen(true);
  };

  // Close the publisher stats modal
  const handleCloseStatsModal = () => {
    setIsStatsModalOpen(false);
  };

  // Debug selected publisher and modal state
  useEffect(() => {
    console.log('Dashboard - selectedPublisher:', selectedPublisher);
    console.log('Dashboard - isStatsModalOpen:', isStatsModalOpen);
  }, [selectedPublisher, isStatsModalOpen]);

  if (loading) {
    return (
      <Pane display="flex" alignItems="center" justifyContent="center" height="100vh">
        <Spinner />
      </Pane>
    );
  }

  if (authError || error) {
    return (
      <Pane padding={24}>
        <Alert
          intent="danger"
          title="Access Denied"
          marginBottom={16}
        >
          You don't have permission to access the Super Admin Dashboard. This area is restricted to superusers only.
        </Alert>

        <Pane marginBottom={16}>
          <Text>If you believe you should have access, please contact your system administrator.</Text>
        </Pane>

        {errorDetails && (
          <Pane background="tint2" padding={16} borderRadius={4} marginBottom={16}>
            <Heading size={500} marginBottom={8}>Error Details</Heading>
            <Code
              appearance="minimal"
              marginBottom={8}
            >
              {errorDetails.message}
            </Code>
            <Button onClick={() => navigate('/')}>Return to Dashboard</Button>
          </Pane>
        )}
      </Pane>
    );
  }

  return (
    <Pane padding={24}>
      <Heading size={900} marginBottom={24}>Super Admin Dashboard</Heading>

      <Pane marginBottom={24}>
        <Text>You're Super!</Text>
      </Pane>

      {/* Publisher Search Bar */}
      <Pane marginBottom={24} background="tint1" padding={16} borderRadius={4}>
        <Heading size={500} marginBottom={8}>Publisher Search</Heading>
        <SuperAdminSearchBar onSelectPublisher={handleSelectPublisher} />
      </Pane>

      {/* Publisher Stats Modal */}
      <PublisherStatsModal
        isShown={isStatsModalOpen}
        onClose={handleCloseStatsModal}
        publisher={selectedPublisher}
      />

      {/* System Stats */}
      <Heading size={700} marginBottom={16}>System Overview</Heading>
      <SystemStatsBox />

      {/* Recent Activity */}
      <Heading size={700} marginTop={32} marginBottom={16}>Recent Activity</Heading>

      {/* Recent Visitors Box */}
      <VisitorsBox />

      {/* Recent Inactive Users Box */}
      <InactiveUsersBox onSelectPublisher={handleSelectPublisher} />

      {/* Recent Signups and Subscriptions side by side */}
      <Pane display="flex" flexWrap="wrap" marginBottom={16}>
        <Pane width="50%" paddingRight={8}>
          <SignupsBox onSelectPublisher={handleSelectPublisher} />
        </Pane>
        <Pane width="50%" paddingLeft={8}>
          <SubscriptionsBox onSelectPublisher={handleSelectPublisher} />
        </Pane>
      </Pane>

      {/* Recent Products and Customers side by side */}
      <Pane display="flex" flexWrap="wrap">
        <Pane width="50%" paddingRight={8}>
          <ProductsBox onSelectPublisher={handleSelectPublisher} />
        </Pane>
        <Pane width="50%" paddingLeft={8}>
          <CustomersBox onSelectPublisher={handleSelectPublisher} />
        </Pane>
      </Pane>

      {/* System Health Monitoring at the bottom */}
      <Heading size={700} marginTop={32} marginBottom={16}>System Health Monitoring</Heading>
      <SystemHealthMonitor />

      {/* Admin Tools Section */}
      <Heading size={700} marginTop={32} marginBottom={16}>Admin Tools</Heading>
      <Pane display="flex" flexWrap="wrap" marginBottom={24}>
        <Card
          elevation={2}
          backgroundColor="white"
          width={300}
          height={120}
          marginRight={16}
          marginBottom={16}
          padding={16}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          cursor="pointer"
          onClick={() => navigate('/super/tools/royalty-calculator')}
          hoverElevation={3}
        >
          <Heading size={500}>Royalty Calculator</Heading>
          <Text>Simulate and verify royalty calculations with detailed breakdown</Text>
        </Card>
      </Pane>
    </Pane>
  );
};

export default SuperAdminDashboard;
